import { useTranslation } from 'react-i18next'
import { useMemo } from 'react'

import { Button, Flex, IllustratedMessage } from '@percent/lemonade'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import type { GivingCampaignGroupPartnerDto } from '@percent/partner-dashboard/services/workplaceGiving/workplaceGivingService.types'
import { useCampaignGroupsTable } from './useCampaignGroupsTable'
import { GroupsTable } from './GroupsTable/GroupsTable'
import { GroupActionsDialogsProvider, useGroupActionsDialogs } from './GroupActionsDialogs/GroupActionsDialogsProvider'

const MAX_ACTIVE_CAMPAIGN_GROUPS = 3

export function GroupsTab() {
  const { data, isLoading, errorMessage, refresh } = useCampaignGroupsTable()

  return (
    <GroupActionsDialogsProvider refresh={refresh}>
      <GroupsTabContent data={data} isLoading={isLoading} errorMessage={errorMessage} />
    </GroupActionsDialogsProvider>
  )
}

type GroupsTabContentProps = Readonly<{
  data: GivingCampaignGroupPartnerDto[]
  isLoading: boolean
  errorMessage: string | undefined
}>

function GroupsTabContent({ data, isLoading, errorMessage }: GroupsTabContentProps) {
  const { t } = useTranslation()
  const { createGroup } = useGroupActionsDialogs()

  const isCreateDisabled = useMemo(() => {
    const activeGroupsCount = data?.filter(group => group.deletedAt === null).length ?? 0

    return activeGroupsCount >= MAX_ACTIVE_CAMPAIGN_GROUPS
  }, [data])

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  if (!isLoading && (!data || data.length === 0)) {
    return (
      <Flex direction="column" align="center" justify="center" p="md" style={{ minHeight: 628 }}>
        <IllustratedMessage
          illustration="nothing-to-see"
          title={t('typography.noGroupsTitle')}
          description={t('typography.noGroupsDescription')}
          cta={{ label: t('button.createGroup'), onClick: () => createGroup() }}
        />
      </Flex>
    )
  }

  return (
    <Flex direction="column">
      <Flex justify="flex-end" p="md">
        <Button
          disabled={isCreateDisabled}
          onClick={() => {
            if (!isCreateDisabled) createGroup()
          }}
        >
          {t('button.createGroup')}
        </Button>
      </Flex>
      <GroupsTable data={data ?? []} isLoading={isLoading} totalResults={data?.length ?? null} />
    </Flex>
  )
}
